import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { id } from 'date-fns/locale';
import API, { resolvePhoto, formatAuthorName } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import './ArticlePage.css';
import { ArrowLeft, Clock, ChevronDown, ChevronUp, Moon, Sun, LogOut, LayoutDashboard, PenSquare, UserCircle, Share2, Bookmark, Volume2, Pause, Minus, Plus, Link2, Send, X as XIcon, MessageCircle, Instagram } from 'lucide-react';
import toast from 'react-hot-toast';
import InspiraFooter from '../components/InspiraFooter';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { useTheme } from '../context/ThemeContext';
import { useRef } from 'react';
import useSEO from '../hooks/useSEO';

function ArticleNavbar() {
  const { user, logout } = useAuth();
  const { settings } = useSiteSettings();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const navLogo = (theme === 'dark' && (settings.logoArticleNavbarDarkUrl || settings.logoDarkUrl))
    ? (settings.logoArticleNavbarDarkUrl || settings.logoDarkUrl)
    : (settings.logoArticleNavbarUrl || settings.logoUrl || '/logo-inspira.png');
  const [showUser, setShowUser] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, right: 0 });
  const userRef = useRef(null);
  const btnRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fn = (e) => {
      if (
        btnRef.current && !btnRef.current.contains(e.target) &&
        dropdownRef.current && !dropdownRef.current.contains(e.target)
      ) setShowUser(false);
    };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  const handleToggle = () => {
    if (!showUser && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 8, right: window.innerWidth - rect.right });
    }
    setShowUser(v => !v);
  };

  const getDash = () => {
    if (!user) return '/login';
    if (user.role === 'editor') return '/editor';
    if (user.role === 'producer') return '/producer';
    if (user.role === 'admin') return '/admin';
    return '/dashboard';
  };

  return (
    <nav className="article-navbar" style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 200,
      background: 'var(--in-glass)', backdropFilter: 'blur(16px)',
      borderBottom: '1px solid var(--in-border)',
      height: 60, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      boxShadow: 'var(--in-shadow)', overflow: 'visible',
    }}>
      <div className="article-navbar-left" style={{ display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 }}>
        <Link to="/" style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img
            src={navLogo}
            alt={settings.siteName}
            style={{ height: settings.logoArticleNavbarSize || 32, objectFit: 'contain', maxWidth: 150 }}
            onError={e => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'block'; }}
          />
          <span style={{ display: 'none', fontWeight: 900, fontSize: 16, color: 'var(--in-blue)' }}>{settings.siteName}</span>
        </Link>
        <Link to="/" className="article-navbar-home" style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--in-text-muted)', textDecoration: 'none', flexShrink: 0 }}>
          <ArrowLeft size={14} /> <span className="article-navbar-home-text">Beranda</span>
        </Link>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {user ? (
          <div ref={userRef} style={{ position: 'relative' }}>
            <button
              ref={btnRef}
              onClick={handleToggle}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: 'var(--in-bg-3)', border: '1px solid var(--in-border)', borderRadius: 8, cursor: 'pointer', color: 'var(--in-text)', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' }}
            >
              {user.avatar
                ? <img src={user.avatar} alt="" style={{ width: 22, height: 22, borderRadius: '50%', objectFit: 'cover' }} />
                : <div style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--in-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>{user.name?.[0] ?? '?'}</div>
              }
              <span>{user.name.split(' ')[0]}</span>
              <ChevronDown size={12} />
            </button>
            {showUser && createPortal(
              <div ref={dropdownRef} style={{ position: 'fixed', top: dropdownPos.top, right: dropdownPos.right, width: 200, background: 'var(--in-card)', border: '1px solid var(--in-border)', borderRadius: 10, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', zIndex: 9999 }}>
                <div style={{ padding: '10px 16px', borderBottom: '1px solid var(--in-border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--in-text)' }}>{user.name}</div>
                  <div style={{ fontSize: 11, color: 'var(--in-text-faint)', textTransform: 'capitalize', marginTop: 2 }}>{user.role}</div>
                </div>
                {[
                  { icon: <LayoutDashboard size={14} />, label: 'Dashboard', to: getDash() },
                  { icon: <UserCircle size={14} />, label: 'Profil Saya', to: '/profil' },
                  ...(user?.role === 'reader' ? [{ icon: <PenSquare size={14} />, label: 'Kirim Artikel', to: '/publikasi' }] : []),
                ].map(item => (
                  <Link key={item.to} to={item.to} onClick={() => setShowUser(false)}
                    style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, color: 'var(--in-text-muted)', textDecoration: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--in-bg-3)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}>
                    {item.icon} {item.label}
                  </Link>
                ))}
                <button onClick={() => { logout(); setShowUser(false); navigate('/'); window.scrollTo({ top: 0 }); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 16px', fontSize: 13, color: 'var(--in-text)', background: 'none', border: 'none', borderTop: '1px solid #f0f0f0', width: '100%', cursor: 'pointer', fontFamily: 'inherit' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--in-bg)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <LogOut size={14} /> Keluar
                </button>
              </div>,
              document.body
            )}
          </div>
        ) : (
          <>
            <Link to="/login" className="btn btn-ghost btn-sm">Masuk</Link>
            <Link to="/register" className="btn btn-primary btn-sm">Daftar</Link>
          </>
        )}
      </div>
    </nav>
  );
}

function ArticleSEO({ article }) {
  const { settings } = useSiteSettings();
  const cover = resolvePhoto(article.photos?.[article.featuredPhoto || 0]?.url);
  // seoTitle auto-generate versi lama terpotong "…" — pakai judul lengkap; seoTitle manual tetap dipakai
  const autoTruncated = article.seoTitle?.endsWith('…') && (article.title || '').startsWith(article.seoTitle.slice(0, -1).trim());
  const seoTitle = (!article.seoTitle || autoTruncated) ? article.title : article.seoTitle;
  const desc = article.seoDescription || article.excerpt || settings.siteDescription;
  const keywords = article.seoKeywords || [article.focusKeyword, ...(article.tags || [])].filter(Boolean).join(', ');
  const siteUrl = settings.siteUrl || window.location.origin;
  const articleUrl = `${siteUrl}/berita/${article.slug}`;

  useSEO({
    title: seoTitle, description: desc, image: cover, url: articleUrl, type: 'article',
    siteName: settings.siteName,
    seo: { ...(settings.seo || {}), keywords: keywords || settings.seo?.keywords },
  });

  useEffect(() => {
    const authorName = formatAuthorName(article.assignedEditor?.name || article.author?.name) || settings.siteName;
    const jsonLd = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: seoTitle,
      description: desc,
      image: cover ? [cover] : undefined,
      datePublished: article.publishedAt || article.createdAt,
      dateModified: article.updatedAt,
      author: { '@type': 'Person', name: authorName },
      publisher: {
        '@type': 'Organization',
        name: settings.seo?.publisherName || settings.seo?.organizationName || settings.siteName,
        logo: { '@type': 'ImageObject', url: settings.seo?.organizationLogo || settings.logoPortalUrl || settings.logoUrl || `${siteUrl}/logo-inspira.png` },
      },
      mainEntityOfPage: { '@type': 'WebPage', '@id': articleUrl },
      ...(article.category?.name ? { articleSection: article.category.name } : {}),
      ...(article.readingTime ? { timeRequired: `PT${article.readingTime}M` } : {}),
      ...(keywords ? { keywords } : {}),
    };
    // Breadcrumb: Beranda › Kategori › Judul
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Beranda', item: siteUrl },
        ...(article.category?.name
          ? [{ '@type': 'ListItem', position: 2, name: article.category.name, item: `${siteUrl}/?category=${article.category._id || ''}` }]
          : []),
        { '@type': 'ListItem', position: article.category?.name ? 3 : 2, name: article.title, item: articleUrl },
      ],
    };

    const inject = (id, data) => {
      let script = document.getElementById(id);
      if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(data);
      return script;
    };
    const s1 = inject('article-json-ld', jsonLd);
    const s2 = inject('breadcrumb-json-ld', breadcrumb);
    return () => { s1?.remove(); s2?.remove(); };
  }, [article, cover, desc, siteUrl, articleUrl, settings]);

  return null;
}

function FloatingActions({ speaking, onTalkback, bookmarked, onBookmark, onFontUp, onFontDown, shareLinks, onCopy, onInstagram, theme, onToggleTheme }) {
  const [shareOpen, setShareOpen] = useState(false);
  const [expanded, setExpanded] = useState(true);

  // Saat diminimize: hanya satu tombol untuk expand kembali
  if (!expanded) {
    return (
      <div className="art-fab-wrap">
        <button className="art-fab art-fab-toggle" title="Tampilkan alat baca" onClick={() => setExpanded(true)}>
          <ChevronUp size={18} />
        </button>
      </div>
    );
  }

  return (
    <div className="art-fab-wrap">
      {shareOpen && (
        <div className="art-fab-share">
          <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="art-fab-share-item" title="WhatsApp"><MessageCircle size={18} /></a>
          <a href={shareLinks.x} target="_blank" rel="noopener noreferrer" className="art-fab-share-item" title="X"><XIcon size={18} /></a>
          <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" className="art-fab-share-item" title="Telegram"><Send size={18} /></a>
          <button className="art-fab-share-item" title="Instagram" onClick={() => { onInstagram(); setShareOpen(false); }}><Instagram size={18} /></button>
          <button className="art-fab-share-item" title="Salin tautan" onClick={() => { onCopy(); setShareOpen(false); }}><Link2 size={18} /></button>
        </div>
      )}
      <button className="art-fab" title="Bagikan" onClick={() => setShareOpen(v => !v)}><Share2 size={18} /></button>
      <button className={`art-fab${bookmarked ? ' active' : ''}`} title="Bookmark" onClick={onBookmark}><Bookmark size={18} fill={bookmarked ? 'currentColor' : 'none'} /></button>
      <button className="art-fab" title="Perbesar huruf" onClick={onFontUp}><Plus size={18} /></button>
      <button className="art-fab" title="Perkecil huruf" onClick={onFontDown}><Minus size={18} /></button>
      <button className={`art-fab${speaking ? ' active' : ''}`} title="Dengarkan artikel" onClick={onTalkback}>{speaking ? <Pause size={18} /> : <Volume2 size={18} />}</button>
      <button className="art-fab" title={theme === 'dark' ? 'Mode terang' : 'Mode gelap'} onClick={onToggleTheme}>{theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}</button>
      <button className="art-fab art-fab-toggle" title="Sembunyikan alat baca" onClick={() => { setShareOpen(false); setExpanded(false); }}><ChevronDown size={18} /></button>
    </div>
  );
}

export default function ArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [fontScale, setFontScale] = useState(1);          // 0.85 – 1.4
  const [speaking, setSpeaking] = useState(false);
  const [bookmarked, setBookmarked] = useState(false);
  const [reactions, setReactions] = useState({ keren: 0, inspiratif: 0, wow: 0, belajar_baru: 0 });
  const [reacted, setReacted] = useState(null);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
    setLoading(true);
    API.get(`/articles/feed/${slug}`)
      .then(r => {
        setArticle(r.data.article);
        setReactions(r.data.article?.reactions || { keren: 0, inspiratif: 0, wow: 0, belajar_baru: 0 });
        setLoading(false);
        setBookmarked(!!user?.bookmarks?.some(b => String(b) === String(r.data.article?._id)));
      })
      .catch(() => setLoading(false));
    API.get(`/articles/feed/${slug}/related`).then(r => setRelated(r.data.articles || [])).catch(() => {});
  }, [slug, user]);

  // Progress bar baca
  useEffect(() => {
    const onScroll = () => {
      const h = document.documentElement;
      const scrolled = h.scrollTop;
      const total = h.scrollHeight - h.clientHeight;
      setProgress(total > 0 ? Math.min(100, (scrolled / total) * 100) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Hentikan talkback saat keluar halaman
  useEffect(() => {
    // Preload daftar voices (sebagian browser perlu trigger awal)
    try { window.speechSynthesis?.getVoices(); } catch {}
    return () => { try { window.speechSynthesis?.cancel(); } catch {} };
  }, []);

  const articleUrlAbs = typeof window !== 'undefined' ? window.location.href : '';

  // Pilih voice: utamakan perempuan + Bahasa Indonesia. Voices bisa load async.
  const pickVoice = () => {
    const voices = window.speechSynthesis.getVoices() || [];
    const idVoices = voices.filter(v => /id[-_]ID|^id$|indones/i.test(v.lang) || /indones/i.test(v.name));
    const femaleHints = /female|wanita|perempuan|damayanti|gadis|sri|dewi|google bahasa indonesia|^id-id-standard-a|wavenet-a|standard-a/i;
    // 1) ID + jelas perempuan  2) ID apa pun  3) voice perempuan apa pun  4) default
    return (
      idVoices.find(v => femaleHints.test(v.name)) ||
      idVoices.find(v => femaleHints.test(v.voiceURI)) ||
      idVoices[0] ||
      voices.find(v => femaleHints.test(v.name)) ||
      null
    );
  };

  const speakChunks = (text) => {
    const synth = window.speechSynthesis;
    const voice = pickVoice();
    // Pecah jadi kalimat agar tidak terpotong (bug Chrome teks panjang) & jeda alami
    const chunks = text.match(/[^.!?]+[.!?]*/g) || [text];
    let i = 0;
    const speakNext = () => {
      if (i >= chunks.length) { setSpeaking(false); return; }
      const part = chunks[i].trim();
      i++;
      if (!part) { speakNext(); return; }
      const u = new SpeechSynthesisUtterance(part);
      u.lang = 'id-ID';
      if (voice) u.voice = voice;
      u.rate = 1; u.pitch = 1.05; // sedikit lebih tinggi → kesan feminin
      u.onend = speakNext;
      u.onerror = () => { setSpeaking(false); };
      synth.speak(u);
    };
    speakNext();
  };

  const toggleTalkback = () => {
    const synth = window.speechSynthesis;
    if (!synth) { toast.error('Browser tidak mendukung pembacaan suara.'); return; }
    if (speaking) { synth.cancel(); setSpeaking(false); return; }
    const text = `${article.title}. ${(article.content || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}`;
    synth.cancel();
    setSpeaking(true);
    // Voices kadang belum siap saat pertama dipanggil — tunggu jika kosong
    if (!synth.getVoices().length) {
      const onReady = () => { speakChunks(text); synth.onvoiceschanged = null; };
      synth.onvoiceschanged = onReady;
      // Fallback jika event tak terpicu
      setTimeout(() => { if (synth.getVoices().length) { synth.onvoiceschanged = null; speakChunks(text); } }, 300);
    } else {
      speakChunks(text);
    }
  };

  const changeFont = (d) => setFontScale(s => Math.max(0.85, Math.min(1.4, +(s + d).toFixed(2))));

  const shareLinks = {
    whatsapp: `https://wa.me/?text=${encodeURIComponent((article?.title || '') + ' ' + articleUrlAbs)}`,
    x: `https://twitter.com/intent/tweet?text=${encodeURIComponent(article?.title || '')}&url=${encodeURIComponent(articleUrlAbs)}`,
    telegram: `https://t.me/share/url?url=${encodeURIComponent(articleUrlAbs)}&text=${encodeURIComponent(article?.title || '')}`,
  };
  const copyLink = () => { navigator.clipboard?.writeText(articleUrlAbs); toast.success('Tautan disalin'); };

  // Instagram tidak punya web share URL. Coba native share (mobile), jika tidak,
  // salin tautan + arahkan buka Instagram untuk ditempel ke story/bio.
  const shareInstagram = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: article?.title, url: articleUrlAbs }); return; } catch { /* dibatalkan */ }
    }
    try { await navigator.clipboard?.writeText(articleUrlAbs); } catch {}
    toast.success('Tautan disalin. Tempel di Instagram Story atau bio Anda.');
    window.open('https://www.instagram.com/', '_blank', 'noopener,noreferrer');
  };

  const toggleBookmark = async () => {
    if (!user) { toast.error('Masuk untuk menyimpan bookmark.'); navigate('/login'); return; }
    try {
      const res = await API.post(`/articles/${article._id}/bookmark`);
      setBookmarked(res.data.bookmarked);
      toast.success(res.data.bookmarked ? 'Disimpan ke bookmark' : 'Dihapus dari bookmark');
    } catch { toast.error('Gagal menyimpan bookmark'); }
  };

  const react = async (type) => {
    if (reacted) return; // 1 reaksi per kunjungan
    setReactions(r => ({ ...r, [type]: (r[type] || 0) + 1 }));
    setReacted(type);
    try { await API.post(`/articles/feed/${slug}/react`, { type }); } catch {}
  };

  if (loading) return (
    <div style={{ minHeight: '100vh', background: 'var(--in-bg)' }}>
      <ArticleNavbar />
      <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px' }}>
        <div className="shimmer" style={{ height: 400, borderRadius: 16, marginBottom: 24 }} />
        <div className="shimmer" style={{ height: 40, marginBottom: 12, width: '80%' }} />
        <div className="shimmer" style={{ height: 20, marginBottom: 8 }} />
        <div className="shimmer" style={{ height: 20, width: '60%' }} />
      </div>
    </div>
  );

  if (!article) return (
    <div style={{ minHeight: '100vh', background: 'var(--in-bg)', display: 'flex', flexDirection: 'column' }}>
      <ArticleNavbar />
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16, color: 'var(--in-text-muted)', padding: 40 }}>
        <div style={{ fontSize: 48, opacity: 0.2 }}>📄</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: 'var(--in-text)' }}>Artikel tidak ditemukan</div>
        <p style={{ fontSize: 14, color: 'var(--in-text-muted)' }}>Artikel ini mungkin belum diterbitkan atau sudah dihapus.</p>
        <Link to="/" className="btn btn-primary">Kembali ke Beranda</Link>
      </div>
    </div>
  );

  const cat = article.category;
  const cover = resolvePhoto(article.photos?.[article.featuredPhoto || 0]?.url);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--in-bg-2)', display: 'flex', flexDirection: 'column' }}>
      <ArticleSEO article={article} />
      <ArticleNavbar />

      {/* Progress bar baca — fixed tepat di bawah navbar */}
      <div style={{ position: 'fixed', top: 60, left: 0, right: 0, zIndex: 199, height: 3, background: 'transparent' }}>
        <div style={{ height: '100%', width: `${progress}%`, background: 'var(--in-orange)', transition: 'width 0.1s linear' }} />
      </div>

      {/* Floating action buttons — kanan bawah, ikut scroll */}
      <FloatingActions
        speaking={speaking} onTalkback={toggleTalkback}
        bookmarked={bookmarked} onBookmark={toggleBookmark}
        onFontUp={() => changeFont(0.1)} onFontDown={() => changeFont(-0.1)}
        shareLinks={shareLinks} onCopy={copyLink} onInstagram={shareInstagram}
        theme={theme} onToggleTheme={toggleTheme}
      />

      {/* Spacer untuk navbar fixed */}
      <div style={{ height: 60, flexShrink: 0 }} />

      <article style={{ flex: 1 }}>
        {/* Cover hero */}
        {cover && (
          <div style={{ width: '100%', maxHeight: 500, overflow: 'hidden' }}>
            <img
              src={cover}
              alt={article.title}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', maxHeight: 500 }}
            />
          </div>
        )}

        <div style={{ maxWidth: 760, margin: '0 auto', padding: '48px 24px 80px' }}>
          {/* Category — tampilkan semua kategori (multi) */}
          {(article.categories?.length ? article.categories : (cat ? [cat] : [])).length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
              {(article.categories?.length ? article.categories : [cat]).map(c => c && (
                <span key={c._id} style={{
                  fontSize: 11, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase',
                  color: c.color || 'var(--in-blue)',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                  <span style={{ width: 3, height: 12, background: c.color || 'var(--in-orange)', borderRadius: 2, display: 'inline-block' }} />
                  {c.name}
                </span>
              ))}
            </div>
          )}

          {/* Title */}
          <h1 style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(24px, 4.5vw, 40px)',
            fontWeight: 900, lineHeight: 1.22, marginBottom: 16,
            color: 'var(--in-text)', letterSpacing: -0.5,
          }}>
            {article.title}
          </h1>

          {/* Excerpt */}
          {article.excerpt && (
            <p style={{
              fontSize: 18, color: 'var(--in-text-muted)', lineHeight: 1.75,
              fontStyle: 'italic', marginBottom: 24, borderLeft: '3px solid var(--in-orange)',
              paddingLeft: 16,
            }}>
              {article.excerpt}
            </p>
          )}

          {/* Meta */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 16,
            padding: '14px 0', borderTop: '1px solid var(--in-border)',
            borderBottom: '1px solid var(--in-border)', marginBottom: 36,
            fontSize: 13, color: 'var(--in-text-faint)', flexWrap: 'wrap',
          }}>
            {article.assignedEditor?.name && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                {article.assignedEditor.avatar
                  ? <img src={resolvePhoto(article.assignedEditor.avatar)} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                  : <span style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--in-blue)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700 }}>{article.assignedEditor.name[0]}</span>
                }
                Oleh <strong style={{ color: 'var(--in-text-muted)' }}>{formatAuthorName(article.assignedEditor.name)}</strong>
              </span>
            )}
            {article.publishedAt && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={12} />
                {format(new Date(article.publishedAt), "d MMMM yyyy, HH.mm", { locale: id })} WIB
              </span>
            )}
          </div>

          {/* Tombol talkback sebelum artikel */}
          <button onClick={toggleTalkback} className="btn btn-secondary btn-sm" style={{ marginBottom: 24, display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {speaking ? <Pause size={15} /> : <Volume2 size={15} />}
            {speaking ? 'Hentikan pembacaan' : 'Dengarkan artikel'}
          </button>

          {/* Content */}
          <div className="article-body" style={{ fontSize: `${fontScale}em` }} dangerouslySetInnerHTML={{ __html: article.content }} />

          {/* Reaksi pembaca */}
          <div className="art-reactions">
            <span className="art-reactions-q">Gimana menurut kamu?</span>
            <div className="art-reactions-list">
              {[
                { key: 'keren', emoji: '👏', label: 'Keren' },
                { key: 'inspiratif', emoji: '🔥', label: 'Inspiratif' },
                { key: 'wow', emoji: '😲', label: 'Wow' },
                { key: 'belajar_baru', emoji: '💡', label: 'Belajar baru' },
              ].map(r => (
                <button key={r.key} onClick={() => react(r.key)} disabled={!!reacted}
                  className={`art-reaction-chip${reacted === r.key ? ' active' : ''}`}>
                  <span>{r.emoji}</span> {r.label} <strong>{reactions[r.key] || 0}</strong>
                </button>
              ))}
            </div>
          </div>

          {/* Share row */}
          <div className="art-share-row">
            <a href={shareLinks.whatsapp} target="_blank" rel="noopener noreferrer" className="art-share-btn">
              <MessageCircle size={15} /> Bagikan ke WhatsApp
            </a>
            <a href={shareLinks.x} target="_blank" rel="noopener noreferrer" className="art-share-btn">
              <XIcon size={15} /> X
            </a>
            <a href={shareLinks.telegram} target="_blank" rel="noopener noreferrer" className="art-share-btn">
              <Send size={15} /> Telegram
            </a>
            <button onClick={shareInstagram} className="art-share-btn">
              <Instagram size={15} /> Instagram
            </button>
            <button onClick={copyLink} className="art-share-btn">
              <Link2 size={15} /> Salin tautan
            </button>
          </div>

          {/* Baca juga */}
          {related.length > 0 && (
            <div className="art-related">
              <div className="art-related-head">Baca Juga</div>
              <div className="art-related-grid">
                {related.map(a => (
                  <Link key={a._id} to={`/berita/${a.slug || a._id}`} className="art-related-card">
                    {(a.photos?.[a.featuredPhoto || 0]?.url) && (
                      <img src={resolvePhoto(a.photos[a.featuredPhoto || 0].url)} alt="" className="art-related-img" />
                    )}
                    <div className="art-related-body">
                      {a.category?.name && <div className="art-related-cat" style={{ color: a.category.color || 'var(--in-blue)' }}>{a.category.name}</div>}
                      <div className="art-related-title">{a.title}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Kembali */}
          <div style={{ marginTop: 56, paddingTop: 24, borderTop: '1px solid var(--in-border)', display: 'flex', gap: 12 }}>
            <Link to="/" className="btn btn-secondary">
              <ArrowLeft size={14} /> Kembali ke Beranda
            </Link>
            {(!user || user.role === 'reader') && (
              <Link to={user ? '/publikasi' : '/register'} className="btn btn-primary">
                Kirim Artikel Anda
              </Link>
            )}
          </div>
        </div>
      </article>

      <InspiraFooter />
    </div>
  );
}
